import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  Ban,
  Copy,
  Globe,
  Link2,
  NotebookPen,
  Plus,
  RotateCcw,
  Search,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/Toaster';
import { Avatar } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { api, getToken } from '../lib/api';
import { cn } from '../lib/cn';
import {
  type DomainHealth,
  type DomainStatus,
  activeDefault,
  useDomainHealth,
  useDomains,
} from '../lib/domains';

type SortKey = 'created' | 'name' | 'links';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 10;

export const Route = createFileRoute('/domains')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: DomainsPage,
});

function fmtBindTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const RED = { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' };
const HEALTH_META: Record<DomainStatus, { label: string; dot: string; text: string }> = {
  healthy: { label: '正常', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  suspended: { label: '已暂停', ...RED },
  dead: { label: '已失效', ...RED },
  unknown: { label: '未知', dot: 'bg-zinc-400', text: 'text-zinc-500' },
};

// 已绑定链接数。isDefault 行额外把 domain='' (跟随默认) 的链接计入。
function LinkCount({ bound, follow }: { bound: number; follow: number }) {
  const total = bound + follow;
  const title = follow
    ? `共 ${total} 条链接（含 ${follow} 条跟随默认域名）`
    : `已绑定 ${total} 条链接`;
  return (
    <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap" title={title}>
      <Link2 className="size-3 shrink-0" />
      {total} 条
    </span>
  );
}

function HealthDot({ h, loading }: { h?: DomainHealth; loading: boolean }) {
  if (!h) return <span className="text-xs text-zinc-400">{loading ? '检测中…' : '—'}</span>;
  const m = HEALTH_META[h.status];
  return (
    <span
      className={cn('inline-flex items-center gap-1 whitespace-nowrap text-xs', m.text)}
      title={h.detail}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', m.dot)} />
      {m.label}
    </span>
  );
}

// 接入引导里的步骤序号圆点。
function StepNum({ n }: { n: number }) {
  return (
    <span className="bg-primary text-primary-foreground mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
      {n}
    </span>
  );
}

// 一行 DNS 记录字段：左侧字段名，右侧值（mono 为字面值，可选复制按钮）。
function DnsRow({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0">
      <span className="text-muted-foreground w-9 shrink-0 text-xs">{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 break-all text-xs',
          mono ? 'font-mono' : 'text-muted-foreground',
        )}
      >
        {value}
      </span>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          title="复制"
          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
        >
          <Copy className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function DomainsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useDomains();
  const [addOpen, setAddOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newNote, setNewNote] = useState('');
  // 行内备注编辑：当前编辑的域名 + 草稿（同一时刻只编辑一行）
  const [noteEditing, setNoteEditing] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  // 搜索 / 排序 / 分页（数据来自 CF 一次拉全量，纯前端处理）
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const cnameTarget = data?.default ?? 'smart-link-page.pages.dev';

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('已复制', `${text} 已复制到剪贴板`);
  };

  // 不再把项目默认 (pages.dev) 置顶；直接列自定义域名。
  // 默认域名行与链接 URL 构建共用同一判定（activeDefault：手动优先，否则第一个未停用）。
  // 搜索/排序会打乱顺序，默认行按域名名匹配而非下标。
  const rows = data?.domains ?? [];
  const preferred = data?.preferredDefault ?? '';
  const defaultName = data ? activeDefault(data) : '';

  // 搜索：域名 / 备注 / 创建人
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.note ?? '').toLowerCase().includes(q) ||
        (d.createdByName ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'links':
          return ((a.linkCount ?? 0) - (b.linkCount ?? 0)) * dir;
        default: {
          const ta = a.createdOn ? Date.parse(a.createdOn) : 0;
          const tb = b.createdOn ? Date.parse(b.createdOn) : 0;
          return (ta - tb) * dir;
        }
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  // 搜索/排序变化时回到第 1 页
  // biome-ignore lint/correctness/useExhaustiveDependencies: 故意以搜索/排序变化为触发，回调只重置页码
  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir]);

  // 服务身份健康检测：每个域名标出 正常 / 已失效 / 被暂停。
  const names = rows.map((d) => d.name);
  const {
    data: health,
    isFetching: healthFetching,
    refetch: recheckHealth,
  } = useDomainHealth(names);
  const healthMap = useMemo(
    () => new Map((health?.results ?? []).map((h) => [h.domain, h])),
    [health],
  );

  const toggleDisable = useMutation({
    mutationFn: (v: { domain: string; disabled: boolean }) =>
      api('/api/domains', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['domains'] });
      toast.success(v.disabled ? '已停用' : '已启用', v.domain);
    },
    onError: (e) => toast.error('操作失败', e instanceof Error ? e.message : '未知错误'),
  });

  // 添加客户自定义域名：后端成功/失败都返回 200 + { ok, error }，据 ok 区分。
  const addDomain = useMutation({
    mutationFn: (v: { domain: string; note: string }) =>
      api<{ ok: boolean; error?: string; domain?: string }>('/api/domains', {
        method: 'POST',
        body: JSON.stringify(v),
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error('添加失败', r.error || '未知错误');
        return;
      }
      qc.invalidateQueries({ queryKey: ['domains'] });
      toast.success('已添加', `${r.domain}（证书签发中，稍后刷新查看状态）`);
      setNewDomain('');
      setNewNote('');
      setAddOpen(false);
    },
    onError: (e) => toast.error('添加失败', e instanceof Error ? e.message : '未知错误'),
  });

  const submitAdd = () => {
    const d = newDomain.trim().toLowerCase().replace(/\.$/, '');
    if (!d || addDomain.isPending) return;
    addDomain.mutate({ domain: d, note: newNote.trim() });
  };

  // 手动指定默认域名（rules 表 domains:default）。domain='' 清除，回到「第一个未停用」。
  const setDefault = useMutation({
    mutationFn: (domain: string) =>
      api<{ ok: boolean; error?: string }>('/api/domains/default', {
        method: 'PUT',
        body: JSON.stringify({ domain }),
      }),
    onSuccess: (r, domain) => {
      if (!r.ok) {
        toast.error('设置失败', r.error || '未知错误');
        return;
      }
      qc.invalidateQueries({ queryKey: ['domains'] });
      toast.success(
        domain ? '已设为默认' : '已取消手动默认',
        domain || '回到自动：第一个未停用域名',
      );
    },
    onError: (e) => toast.error('设置失败', e instanceof Error ? e.message : '未知错误'),
  });

  // 更新已有域名备注（domain_meta upsert，不影响创建人快照）。
  const saveNote = useMutation({
    mutationFn: (v: { domain: string; note: string }) =>
      api<{ ok: boolean; error?: string }>('/api/domains/note', {
        method: 'PUT',
        body: JSON.stringify(v),
      }),
    onSuccess: (r, v) => {
      if (!r.ok) {
        toast.error('保存失败', r.error || '未知错误');
        return;
      }
      qc.invalidateQueries({ queryKey: ['domains'] });
      toast.success('备注已保存', v.domain);
      setNoteEditing(null);
    },
    onError: (e) => toast.error('保存失败', e instanceof Error ? e.message : '未知错误'),
  });

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">域名管理</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            落地页 (smart-link-page) 当前可用的域名，实时来自 Cloudflare
            Pages。停用某域名后，「链接管理」的域名下拉里将不可再选它（已在用的链接不受影响）。
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={healthFetching || names.length === 0}
            onClick={() => recheckHealth()}
            title="重新检测所有域名的实际服务可用性"
          >
            <RotateCcw className={cn('size-3.5', healthFetching && 'animate-spin')} /> 重新检测
          </Button>
          <Button type="button" size="sm" onClick={() => setAddOpen((v) => !v)}>
            {addOpen ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
            {addOpen ? '取消' : '添加域名'}
          </Button>
        </div>
      </div>

      {addOpen && (
        <Card className="mb-3 gap-0 overflow-hidden p-0">
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-medium">添加客户自定义域名</h2>
            <p className="text-muted-foreground mt-0.5 text-xs">两步把客户的域名接入落地页</p>
          </div>

          {/* 步骤 1：DNS 配置 */}
          <div className="flex gap-3 px-4 py-3">
            <StepNum n={1} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">在客户域名的 DNS 处添加一条 CNAME 记录</p>
              <div className="bg-muted/40 mt-2 overflow-hidden rounded-md border">
                <DnsRow label="类型" value="CNAME" mono />
                <DnsRow label="主机" value="客户子域，如 ad、www；或 @ 表示根域" />
                <DnsRow
                  label="目标"
                  value={cnameTarget}
                  mono
                  onCopy={() => copyText(cnameTarget)}
                />
              </div>
            </div>
          </div>

          {/* 步骤 2：填写并提交 */}
          <div className="flex gap-3 border-t px-4 py-3">
            <StepNum n={2} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">DNS 生效后，填入完整域名提交</p>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  autoFocus
                  value={newDomain}
                  placeholder="ad.example.com"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitAdd();
                  }}
                />
                <Button
                  type="button"
                  className="shrink-0"
                  disabled={addDomain.isPending || !newDomain.trim()}
                  onClick={submitAdd}
                >
                  {addDomain.isPending ? '提交中…' : '提交'}
                </Button>
              </div>
              <Input
                className="mt-2"
                value={newNote}
                placeholder="备注（可选，如客户名 / 投放计划）"
                maxLength={500}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitAdd();
                }}
              />
              <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
                提交后域名以「待生效」加入列表，Cloudflare 自动签发证书；状态变为
                <span className="text-foreground font-medium">「已激活」</span>
                后即可在链接管理中选用。
              </p>
            </div>
          </div>
        </Card>
      )}

      {isLoading && <p className="text-muted-foreground text-sm">加载中…</p>}

      {data?.error && (
        <Card className="mb-3 border-amber-200 bg-amber-50 p-4 text-sm text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          {data.error}
        </Card>
      )}

      {/* 工具栏：搜索 + 排序 + 计数 */}
      {!isLoading && rows.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索域名 / 备注 / 创建人"
              className="h-9 pl-8"
            />
          </div>
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger size="sm" className="h-9 w-[124px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created">按绑定时间</SelectItem>
              <SelectItem value="name">按域名</SelectItem>
              <SelectItem value="links">按链接数</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9"
            title={sortDir === 'desc' ? '当前降序，点击切换升序' : '当前升序，点击切换降序'}
            onClick={() => setSortDir((v) => (v === 'asc' ? 'desc' : 'asc'))}
          >
            {sortDir === 'desc' ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUp className="size-3.5" />
            )}
          </Button>
          <span className="text-muted-foreground ml-auto shrink-0 text-xs tabular-nums">
            共 {sorted.length} 个{sorted.length !== rows.length && ` / ${rows.length}`}
          </span>
        </div>
      )}

      <div className="space-y-2">
        {paged.map((d) => {
          const isDefault = !d.disabled && d.name === defaultName;
          return (
            <Card key={d.name} className={cn('gap-1.5 p-3', d.disabled && 'opacity-60')}>
              {/* 第一行：域名 + 状态 + 操作 */}
              <div className="flex items-center gap-2">
                <Globe className="text-muted-foreground size-4 shrink-0" />
                <code
                  className={cn(
                    'min-w-0 flex-1 truncate text-sm font-medium',
                    d.disabled && 'line-through',
                  )}
                >
                  {d.name}
                </code>
                <Badge
                  variant={
                    d.disabled
                      ? 'secondary'
                      : isDefault
                        ? 'info'
                        : d.status === 'active'
                          ? 'success'
                          : 'warning'
                  }
                  title={
                    isDefault
                      ? d.name === preferred
                        ? '手动指定的默认域名'
                        : '自动判定（第一个未停用）'
                      : undefined
                  }
                >
                  {d.disabled
                    ? '已停用'
                    : isDefault
                      ? '默认'
                      : d.status === 'active'
                        ? '已激活'
                        : d.status}
                </Badge>
                {!d.disabled && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-8 shrink-0"
                    disabled={setDefault.isPending}
                    title={
                      d.name === preferred
                        ? '取消手动默认（回到自动：第一个未停用）'
                        : isDefault
                          ? '固定为默认域名（当前为自动判定，新增域名不会顶替）'
                          : '设为默认域名（domain 为空的链接落到这里）'
                    }
                    onClick={() => setDefault.mutate(d.name === preferred ? '' : d.name)}
                  >
                    <Star
                      className={cn(
                        'size-3.5',
                        d.name === preferred && 'fill-amber-400 text-amber-400',
                      )}
                    />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-8 shrink-0"
                  title={d.note ? '编辑备注' : '添加备注'}
                  onClick={() => {
                    if (noteEditing === d.name) {
                      setNoteEditing(null);
                    } else {
                      setNoteEditing(d.name);
                      setNoteDraft(d.note ?? '');
                    }
                  }}
                >
                  <NotebookPen className="size-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={toggleDisable.isPending}
                  onClick={() => toggleDisable.mutate({ domain: d.name, disabled: !d.disabled })}
                  title={d.disabled ? '重新启用该域名' : '停用该域名（下拉将不可选）'}
                >
                  {d.disabled ? (
                    <>
                      <RotateCcw className="size-3.5" /> 启用
                    </>
                  ) : (
                    <>
                      <Ban className="size-3.5" /> 停用
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-8 shrink-0"
                  title="复制域名"
                  onClick={() => copyText(d.name)}
                >
                  <Copy className="size-3.5" />
                </Button>
              </div>

              {/* 第二行：创建人 / 绑定时间 / 链接数 / 健康状态 */}
              <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 text-xs">
                {d.createdByName && (
                  <span
                    className="inline-flex items-center gap-1.5 whitespace-nowrap"
                    title={`由 ${d.createdByName} 添加`}
                  >
                    <Avatar
                      src={d.createdByAvatar}
                      name={d.createdByName}
                      className="size-4 text-[8px]"
                    />
                    <span className="max-w-[100px] truncate">{d.createdByName}</span>
                  </span>
                )}
                {d.createdOn && (
                  <span className="whitespace-nowrap" title="域名绑定时间">
                    绑定于 {fmtBindTime(d.createdOn)}
                  </span>
                )}
                <LinkCount
                  bound={d.linkCount ?? 0}
                  follow={isDefault ? (data?.followDefaultLinkCount ?? 0) : 0}
                />
                <HealthDot h={healthMap.get(d.name)} loading={healthFetching} />
              </div>

              {/* 备注：编辑态显示输入框；否则有备注才显示一行 */}
              {noteEditing === d.name ? (
                <div className="flex items-center gap-2 pl-6">
                  <Input
                    autoFocus
                    className="h-8 text-xs"
                    value={noteDraft}
                    placeholder="备注（如客户名 / 投放计划），留空清除"
                    maxLength={500}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !saveNote.isPending)
                        saveNote.mutate({ domain: d.name, note: noteDraft.trim() });
                      if (e.key === 'Escape') setNoteEditing(null);
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0"
                    disabled={saveNote.isPending}
                    onClick={() => saveNote.mutate({ domain: d.name, note: noteDraft.trim() })}
                  >
                    {saveNote.isPending ? '保存中…' : '保存'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setNoteEditing(null)}
                  >
                    取消
                  </Button>
                </div>
              ) : (
                d.note && (
                  <p className="text-muted-foreground truncate pl-6 text-xs" title={d.note}>
                    {d.note}
                  </p>
                )
              )}
            </Card>
          );
        })}
        {!isLoading && rows.length === 0 && (
          <Card className="text-muted-foreground p-6 text-center text-sm">
            暂无自定义域名{data?.default ? `，链接暂回退到 ${data.default}` : ''}
          </Card>
        )}
        {!isLoading && rows.length > 0 && sorted.length === 0 && (
          <Card className="text-muted-foreground p-6 text-center text-sm">
            没有匹配的域名，试试调整搜索词
          </Card>
        )}
      </div>

      {/* 分页 */}
      {!isLoading && sorted.length > PAGE_SIZE && (
        <div className="text-muted-foreground mt-3 flex items-center justify-between text-sm">
          <span className="tabular-nums">
            第 {(curPage - 1) * PAGE_SIZE + 1}–{Math.min(curPage * PAGE_SIZE, sorted.length)} 个 /
            共 {sorted.length} 个
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={curPage <= 1}
              onClick={() => setPage(curPage - 1)}
            >
              上一页
            </Button>
            <span className="tabular-nums">
              {curPage} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={curPage >= totalPages}
              onClick={() => setPage(curPage + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
